import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Megaphone,
  Info,
  CheckCircle2,
  AlertTriangle,
  AlertOctagon,
  X,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";

/**
 * Platform-wide announcements banner. Shown at the top of every tenant's
 * AppShell (below `AnnouncementBanner`). Broadcasts messages published by
 * platform admins from /app/platform/announcements.
 */

const LEVEL_STYLES = {
  info: {
    border: "border-sky-300",
    bg: "from-sky-50 via-sky-100 to-sky-50",
    text: "text-sky-900",
    icon: Info,
  },
  success: {
    border: "border-emerald-300",
    bg: "from-emerald-50 via-emerald-100 to-emerald-50",
    text: "text-emerald-900",
    icon: CheckCircle2,
  },
  warning: {
    border: "border-amber-300",
    bg: "from-amber-50 via-amber-100 to-amber-50",
    text: "text-amber-900",
    icon: AlertTriangle,
  },
  critical: {
    border: "border-red-300",
    bg: "from-red-50 via-red-100 to-red-50",
    text: "text-red-900",
    icon: AlertOctagon,
  },
};

export default function PlatformAnnouncementBanner() {
  const { user } = useAuthStore();
  const { i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(
        JSON.parse(localStorage.getItem("serpix_platform_ann_dismissed") || "[]"),
      );
    } catch {
      return new Set();
    }
  });

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/platform/announcements/active");
      setItems(data || []);
    } catch {
      /* ignore */
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresh every 2 minutes so new announcements appear soon after publish.
  useEffect(() => {
    const int = setInterval(load, 120_000);
    return () => clearInterval(int);
  }, [load]);

  const persistDismissed = (next) => {
    setDismissed(next);
    localStorage.setItem(
      "serpix_platform_ann_dismissed",
      JSON.stringify(Array.from(next)),
    );
  };

  const visible = useMemo(
    () => items.filter((i) => !dismissed.has(i.id)),
    [items, dismissed],
  );

  if (visible.length === 0) return null;

  const lang = i18n.language === "en" ? "en" : "tr";
  const pickTitle = (a) => (lang === "en" ? a.title_en || a.title_tr : a.title_tr || a.title_en);
  const pickBody = (a) => (lang === "en" ? a.body_en || a.body_tr : a.body_tr || a.body_en);

  return (
    <div data-testid="platform-announcement-banner">
      {visible.map((a) => {
        const st = LEVEL_STYLES[a.level] || LEVEL_STYLES.info;
        const Icon = st.icon;
        const title = pickTitle(a);
        const body = pickBody(a);
        return (
          <div
            key={a.id}
            data-testid={`platform-announcement-${a.id}`}
            className={`flex items-start gap-3 border-b ${st.border} bg-gradient-to-r ${st.bg} px-4 py-2 ${st.text}`}
          >
            <div className="mt-0.5 shrink-0">
              <Icon className="h-4 w-4" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-widest opacity-70">
                  Serpix
                </span>
                <span className="text-sm font-semibold">{title}</span>
              </div>
              {body && (
                <div className="mt-0.5 whitespace-pre-wrap text-xs leading-relaxed opacity-90">
                  {body}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => persistDismissed(new Set([...dismissed, a.id]))}
              data-testid={`platform-announcement-dismiss-${a.id}`}
              className="ml-auto shrink-0 rounded p-1 transition-colors hover:bg-black/5"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
